import { PackageUpgradeModal } from "./components/OfferGate";
import { useFetchHotel } from "./redux/hooks";
import { getOfferCapabilities } from "../../common/offerCapabilities";
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextInput,
  Heading,
  Layer,
  CheckBox,
  FormField,
} from "grommet";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import CategoryService from "@services/category.service";
import PhotoService from "@services/photo.service";
import { PhotoUploader, ValidStatusesSelect } from "./";
import { PriceInput } from "@shared";
import { useTranslation } from "react-i18next";
import ConsumptionScheduleEditor from "./ConsumptionScheduleEditor";

const defaultValues = {
  _id: 0,
  name: "",
  feeLabel: "",
  fee: 0,
  photo: "",
  isMenu: false,
  active: false,
  taxPercent: "",
  validStatuses: ["prearrival", "checkedin"],
  consumptionSchedule: {},
};

export default function AddCategory(props) {
  const { t } = useTranslation();
  const { hotel } = useFetchHotel();
  const [upgrade, setUpgrade] = useState(false);
  const initialActive = useRef(false);
  const { categoryId, onClose } = props;
  const [category, setCategory] = useState(defaultValues);
  const [photo, setPhoto] = useState(undefined);

  function handleInputChange({ target: { name, value } }) {
    var values = { ...category };
    values[name] = value;
    setCategory(values);
  }

  function handleCheckboxChange({ target: { name, checked } }) {
    if (name === "active" && checked && !getOfferCapabilities(hotel).upsells) { setUpgrade(true); return; }
    var values = { ...category };
    values[name] = checked;
    setCategory(values);
  }

  const handleAdd = () => {
    if (category.active && !initialActive.current && !getOfferCapabilities(hotel).upsells) { setUpgrade(true); return; }
    if (categoryId === 0) {
      CategoryService.addCategory(category).then(
        async (response) => {
          const category = response.data && response.data.category;
          if (category) await uploadPhoto(category._id);
          onClose(true);
          presentToastSuccess(t("common.success"));
        },
        (error) => {
          presentToastErrorContent(error);
        },
      );
    } else {
      CategoryService.updateCategory(category).then(
        async () => {
          await uploadPhoto(categoryId);
          onClose(true);
          presentToastSuccess(t("common.success"));
        },
        (error) => {
          presentToastErrorContent(error);
        },
      );
    }
  };

  const uploadPhoto = async (categoryId) => {
    if (!photo) return;
    await CategoryService.uploadPhoto(categoryId, photo).then(
      (response) => {
        if (response.data.success) {
          //presentToastSuccess("Photo Updated");
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  useEffect(() => {
    if (categoryId !== 0) {
      presentLoading();
      CategoryService.getCategory(categoryId).then(
        (response) => {
          dismissLoading();
          const category = response.data;
          if (category._id) {
            initialActive.current = Boolean(category.active);
            setCategory(category);
          }
        },
        (error) => {
          dismissLoading();
          presentToastErrorContent(error);
        },
      );
    } else {
      initialActive.current = false;
      setCategory(defaultValues);
    }
  }, [categoryId]);

  return (
    <Layer
      position="center"
      onClickOutside={() => onClose(false)}
      onEsc={onClose}
    >
      {upgrade && <PackageUpgradeModal capability="upsells" onClose={() => setUpgrade(false)} />}
      <Box
        pad="medium"
        gap="small"
        width="large"
        overflow="scroll"
        style={{ display: "block", maxHeight: 700 }}
      >
        <Box direction="row" justify="between">
          <Heading level={3} margin="none">
            {categoryId === 0
              ? t("categoryDialog.addTitle")
              : t("categoryDialog.editTitle")}
          </Heading>
          <CheckBox
            name="active"
            toggle
            checked={category.active}
            onChange={handleCheckboxChange}
            label={t("common.active")}
          />
        </Box>
        <Box direction="row" gap="medium">
          <Box gap="medium">
            <FormField label={t("categoryDialog.name")} width="medium">
              <TextInput
                name="name"
                value={category.name}
                onChange={handleInputChange}
              />
            </FormField>
            <Box direction="row" gap="small">
              <FormField label={t("categoryDialog.feeLabel")}>
                <TextInput
                  name="feeLabel"
                  value={category.feeLabel}
                  onChange={handleInputChange}
                />
              </FormField>
              <FormField label={t("categoryDialog.feeAmount")}>
                <PriceInput
                  style={{ border: 0 }}
                  name="fee"
                  value={category.fee}
                  onChange={handleInputChange}
                />
              </FormField>
            </Box>
            <CheckBox
              name="isMenu"
              checked={category.isMenu}
              onChange={handleCheckboxChange}
              label={t("categoryDialog.showAsMenu")}
            />
            {category.isMenu && (
              <div>
                <FormField label={t("productDialog.taxRate")}>
                  <TextInput
                    name="taxPercent"
                    value={category.taxPercent}
                    onChange={handleInputChange}
                  />
                </FormField>
                <FormField label={t("productDialog.validStatuses")}>
                  <ValidStatusesSelect
                    name="validStatuses"
                    currentValue={category.validStatuses}
                    onChange={handleInputChange}
                  />
                </FormField>
              </div>
            )}
          </Box>
          <Box alignSelf="center">
            <PhotoUploader
              label={t("productDialog.photoUpload")}
              fitWidth={980}
              fitHeight={600}
              onChange={setPhoto}
              photo={PhotoService.getURL(category.photo) || ""}
            />
          </Box>
        </Box>
        <ConsumptionScheduleEditor
          value={category.consumptionSchedule}
          onChange={(consumptionSchedule) =>
            setCategory({ ...category, consumptionSchedule })
          }
        />
        <Box
          as="footer"
          gap="small"
          direction="row"
          align="center"
          justify="end"
          pad={{ top: "medium", bottom: "small" }}
        >
          <Button
            label={t("common.cancel")}
            onClick={() => onClose(false)}
            color="dark-3"
          />
          <Button
            label={categoryId === 0 ? t("common.add") : t("common.update")}
            onClick={handleAdd}
            primary
            color="status-ok"
          />
        </Box>
      </Box>
    </Layer>
  );
}

AddCategory.propTypes = {};
AddCategory.defaultProps = {};
